import { Button, Card, CardContent, Box, Grid, Stack, Typography, Autocomplete, TextField, Avatar, InputAdornment, List, ListItemIcon, ListItem, ListItemAvatar, ListItemText, ListItemButton, IconButton, Alert, AlertTitle, Tooltip, Chip, lighten, darken } from '@mui/material';
import { useDispatch } from 'react-redux';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import HtmlTooltip from '../../../../components/tooltip/HtmlTooltip';
import React, { useEffect, useState, useMemo, useRef, Suspense } from 'react';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import { DateTime } from 'luxon';

import { useGetPowerLoadProfileAnalysisQuery, useGetPowerLoadProfileAnalysisSeuQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';

const PowerLoadProfilesAnalysisChart = React.lazy(() => import('remote/PowerLoadProfilesAnalysisChart').catch(() => ({ default: () => <div>Failed to load</div> })));

const HeaderDisplay = ({ label, description }) => {
  return (<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 2 }}>
    <Typography variant="body1" color="textSecondary" fontWeight="bold">
      {label}
    </Typography>
    <Tooltip title={description} placement="right">
      <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
    </Tooltip>
  </Box>)
}
const ValueDisplay = ({ label = 'Gennemsnit', value = 0, unit = 'kWh', trend = 0 }) => {

  const trendLabel = `${trend > 0 ? '+' : ''}${trend.toFixed(0)}%`
  const trendColor = (() => {
    if (trend >= 10) return 'error'
    if (trend >= 2) return 'warning'
    if (trend >= -2) return 'primary'
    return 'success'
  })()

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
            {value} {unit}
          </Typography>
          <Chip
            sx={{
              marginLeft: 1,
              fontWeight: 'bold',
              backgroundColor: (theme) => theme.palette.mode === 'light' ? lighten(theme.palette[trendColor].main, 0.9) : darken(theme.palette[trendColor].main, 0.8),
              color: (theme) => theme.palette[trendColor].dark,
              border: (theme) => `1px solid ${theme.palette[trendColor].light}`,
            }}
            label={trendLabel}
            size={'small'}
          />
        </Box>
      </Box>
    </Box>
  );
}

// MAKE CHIP CARD AND MAKE IT LIKE INITIATIVES TAB

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const PowerLoadProfilesAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange
}) => {


  const dispatch = useDispatch()

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null)
  const [seuToAnalyse, setSeuToAnalyse] = useState(null)
  const [analysisMode, setAnalysisMode] = useState('node'); // or 'seu'
  const [filtersOpen, setFiltersOpen] = useState(true);


  const { data: nodeAnalysisData, isFetching: nodeAnalysisDataFetching, isError: nodeAnalysisError } = useGetPowerLoadProfileAnalysisQuery({ accountId: selectedAccount.AccountId, energyGroupId: selectedEnergyGroup.energyGroupId, nodeId: nodeToAnalyse?.energyTreeNodeId, startTimestamp: timeRange.start, endTimestamp: timeRange.end }, { skip: !nodeToAnalyse || !selectedEnergyGroup || !selectedAccount});
  const { data: seuAnalysisData, isFetching: seuAnalysisDataFetching, isError: seuAnalysisError } = useGetPowerLoadProfileAnalysisSeuQuery({ accountId: selectedAccount.AccountId, energyGroupId: selectedEnergyGroup.energyGroupId, seuType: seuToAnalyse, startTimestamp: timeRange.start, endTimestamp: timeRange.end }, { skip: !seuToAnalyse || !selectedEnergyGroup || !selectedAccount});


  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0])
    }
  }, [selectedEnergyGroup])

  useEffect(() => {
    if (nodeAnalysisError || seuAnalysisError)
      dispatch(showSnackbar({severity: 'error', message: 'Der skete en fejl i udregningen af analysen'}))
  }, [nodeAnalysisError, seuAnalysisError])

  const handleApplyFilters = async (filters) => {
    setDurationFilters(filters);
  }
  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      if (!map.has(node.energyTreeNodeId)) {
        map.set(node.energyTreeNodeId, { ...node, children: [] });
      }
      if (node.parentId) {
        if (!map.has(node.parentId)) {
          map.set(node.parentId, { children: [] });
        }
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      const type = node.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(() => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [], [nodeToAnalyse, nodeLookup]);
  const parent = useMemo(() => nodeLookup.get(nodeToAnalyse?.parentId) || null, [nodeToAnalyse, nodeLookup]);

  const seuInputRef = useRef(null);

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([type, nodes]) =>
        nodes.some(node => node.dataStreams?.length > 0)
      )
      .map(([type]) => type)
      .filter(type => type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle');
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse);
    }
  }, [analysisMode]);


  const nodeData = nodeAnalysisData?.PowerLoadProfileData;
  const seuData = seuAnalysisData?.PowerLoadProfileData;

  const nodePowerData = nodeAnalysisData?.PowerData;
  const seuPowerData = seuAnalysisData?.PowerData;

  const loadData = analysisMode === 'node' ? nodeData : seuData;
  const powerData = analysisMode === 'node' ? nodePowerData : seuPowerData

  const nodeSummary = nodeAnalysisData?.PowerLoadProfileSummary;
  const seuSummary = seuAnalysisData?.PowerLoadProfileSummary;

  const summaryData = analysisMode === 'node' ? nodeSummary : seuSummary;


  const analysisError = analysisMode === 'node' ? nodeAnalysisError : seuAnalysisError;

  return (
    <Box>
      <Grid container spacing={2}>

        <Cards data={summaryData} />
        <Grid item xs={12} md={filtersOpen ? 8 : 12} xl={filtersOpen ? 9 : 12} sx={{ transition: 'flex-basis 0.3s ease' }}>
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 600 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {true && (
                        <>
                          <ListItemText
                            primary={analysisMode === 'node'
                              ? `Node`
                              : `SEU`}
                            primaryTypographyProps={{ color: 'text.secondary', variant: 'body2', }}
                            secondary={analysisMode === 'node'
                              ? `${nodeToAnalyse?.name}`
                              : `${seuToAnalyse}`}
                            secondaryTypographyProps={{ color: 'text.primary', variant: 'h6', fontWeight: 'bold' }}
                          />
                          <HtmlTooltip title="Belastningsanalysen viser, hvornår forbruget har ligget på et givent kW-niveau i den valgte periode. Analysen giver blandt andet indsigt i grundbelastning og spidsbelastninger." placement="right">
                            <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                          </HtmlTooltip>
                        </>
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      I perioden {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : 'Vælg periode'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HtmlTooltip title={`${filtersOpen ? 'Skjul' : 'Vis'} filtre`} placement="top">
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, ml: 4 }}
                      >
                        {filtersOpen ? <KeyboardArrowRightRoundedIcon /> : <KeyboardArrowLeftRoundedIcon />}
                      </IconButton>
                    </HtmlTooltip>
                  </Box>

                </Box>
              </Stack>
              {!analysisError ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <PowerLoadProfilesAnalysisChart
                    loadData={loadData}
                    powerData={powerData}
                    isLoading={nodeAnalysisDataFetching || seuAnalysisDataFetching}
                  />
                </Suspense>
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Ingen data tilgængelig</AlertTitle>
                  Det er ikke muligt at vise analysen.
                </Alert>
              )
              }
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen &&
          <Grid item xs={12} md={3}>
            <Grid item xs sx={{ mt: 0 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                    <Grid item xs>
                      {analysisMode === 'node' ? (
                        <>
                          <Autocomplete
                            options={selectedEnergyGroup?.energyTreeNodes || []}
                            getOptionLabel={(option) => option?.name || ""}
                            value={nodeToAnalyse || selectedEnergyGroup?.energyTreeNodes[0] || null}
                            onChange={(event, newValue) => {
                              if (newValue) setNodeToAnalyse(newValue);
                            }}
                            disableClearable
                            renderOption={(props, option) => (

                              <ListItem
                                {...props}
                                key={option.energyTreeNodeId}
                                sx={{
                                  '&:hover .choose-type-btn': {
                                    visibility: 'visible'
                                  },
                                  justifyContent: 'space-between',
                                }}
                                disableGutters
                                secondaryAction={
                                  validSeuOptions.includes(option.type) ? (

                                    <HtmlTooltip
                                      placement="top"
                                      title={
                                        <Box display="flex" alignItems="center">
                                          <ListItemAvatar>
                                            <Avatar
                                              variant="rounded"
                                              sx={{
                                                backgroundColor: getTypeColor(option.type) || "#555",
                                                color: "#fff",
                                                fontWeight: "bold",
                                                borderRadius: 2
                                              }}
                                            >
                                              <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                            </Avatar>
                                          </ListItemAvatar>
                                          <ListItemText
                                            primary={option.type}
                                            secondary={
                                              <>
                                                Der er fundet{' '}
                                                <strong>
                                                  {nodeGroupsByType.get(option.type)?.length}{' '}
                                                  {nodeGroupsByType.get(option.type)?.length === 1
                                                    ? 'målepunkt'
                                                    : 'målepunkter'}
                                                </strong>{' '}
                                                af denne type.
                                              </>
                                            }
                                          />
                                        </Box>
                                      }
                                    >
                                      <Box
                                        className="choose-type-btn"
                                        sx={{
                                          visibility: 'hidden',
                                          mr: 2,
                                          flexShrink: 0
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ color: 'text.secondary', pr: 1 }}
                                        >
                                          {option?.type}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="primary"
                                          sx={{
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSeuToAnalyse(option.type);
                                            // setNodeToAnalyse(null);
                                            setAnalysisMode('seu');
                                          }}
                                        >
                                          Vælg SEU type
                                        </Typography>
                                      </Box>
                                    </HtmlTooltip>
                                  ) : null
                                }
                              >
                                <Box display="flex" alignItems="center">
                                  <ListItemAvatar>
                                    <Avatar
                                      variant="rounded"
                                      sx={{
                                        backgroundColor: getTypeColor(option.type) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}
                                    >
                                      <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText primary={option.name} />
                                </Box>
                              </ListItem>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Vælg målepunkt"
                                variant="outlined"
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-root": { borderRadius: "10px" },
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                                  },
                                  "& .MuiInputLabel-root": { color: "gray" },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: nodeToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar variant="rounded" sx={{
                                        backgroundColor: getTypeColor(nodeToAnalyse.type) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}><i className={`fas ${getTypeIcon(nodeToAnalyse.type)}`}></i></Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />
                          <Box mt={1} display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('seu');
                                // setSeuToAnalyse(null);
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              Vælg SEU typer
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Autocomplete
                            options={validSeuOptions} // SEU types
                            getOptionLabel={(option) => option}
                            value={seuToAnalyse}
                            onChange={(e, newValue) => setSeuToAnalyse(newValue)}
                            disableClearable
                            renderOption={(props, option) => {
                              const isDisabled = ['Hovedmåler', 'Tavle', 'Undertavle'].includes(option);
                              return (
                                <li
                                  {...props}
                                  key={option}
                                  style={{
                                    ...props.style,
                                    opacity: isDisabled ? 0.4 : 1,
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {option}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Vælg SEU type"
                                inputRef={seuInputRef}
                                autoFocus={false}
                                variant="outlined"
                                sx={{ "& .MuiInputBase-root": { borderRadius: "10px" } }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: seuToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar variant="rounded" sx={{
                                        backgroundColor: getTypeColor(seuToAnalyse) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}><i className={`fas ${getTypeIcon(seuToAnalyse)}`}></i></Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />

                          <Box mt={1} display="flex" justifyContent="flex-start">
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('node');
                                // setSeuToAnalyse(null);
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              Vælg målepunkter
                            </Button>
                          </Box>
                        </>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => parent && setNodeToAnalyse(parent)}
                        disabled={!parent}
                        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
                      >
                        <ArrowUpwardRoundedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {analysisMode === 'node' && (
                    <List dense
                      sx={{
                        maxHeight: 425, // or whatever height fits your layout
                        overflowY: 'auto',
                      }}>
                      {children.length > 0 ? (
                        children.map(child => (
                          <ListItemButton key={child.energyGroupId} onClick={() => setNodeToAnalyse(child)} sx={{ borderRadius: 2 }}>
                            <ListItemIcon>
                              <Avatar
                                variant="rounded"
                                size="small"
                                sx={{
                                  backgroundColor: getTypeColor(child.type) || "#555",
                                  color: "#fff",
                                  fontWeight: "bold",
                                  borderRadius: 2
                                }}
                              >
                                <i className={`fas ${getTypeIcon(child.type)}`}></i>
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              secondary={child.type}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'gray' }}>
                          Ingen yderligere neddeling
                        </Typography>
                      )}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        }
      </Grid>
    </Box>
  )
}

export default PowerLoadProfilesAnalysisTab


const Cards = ({ data }) => {
  return (<>

    <Grid item xs={12} md={4}>
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <HeaderDisplay
            label="Grundbelastning"
            description="Viser bygningens laveste og mest stabile elforbrug i løbet af dagen. Værdien er ikke blot den laveste måling, men beregnet ud fra en model, der identificerer typiske forbrugsmønstre over tid. Grundbelastningen giver indblik i det forbrug, der stort set altid er til stede – også uden for brugstid."
          />
          <ValueDisplay
            label="Gennemsnit"
            value={data?.BaseLoadSummary?.Average.toFixed(0)}
            unit="kWh"
            trend={data?.BaseLoadSummary?.TrendPercentage}
          />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={4}>
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <HeaderDisplay
            label="Mellembelastning"
            description="Viser elforbrug i det mellemste niveau – altså det forbrug, der hverken er grundforbrug eller spidsbelastning. Niveauet er beregnet på baggrund af en analysemodel, der udpeger de mest gennemgående mønstre i bygningens daglige elforbrug."
          />
          <ValueDisplay
            label="Gennemsnit"
            value={data?.IntermediateLoadSummary?.Average.toFixed(0)}
            unit="kWh"
            trend={data?.IntermediateLoadSummary?.TrendPercentage}
          />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={4}>

      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <HeaderDisplay
            label="Spidsbelastning"
            description="Viser det højeste niveau af elforbrug, som typisk opstår i kortere perioder i løbet af dagen. Værdien er fundet gennem en model, der ser på forbrugsmønstre og identificerer, hvor energiforbruget topper i praksis – ikke bare i enkeltmålinger."
          />
          <ValueDisplay
            label="Gennemsnit"
            value={data?.PeakLoadSummary?.Average.toFixed(0)}
            unit="kWh"
            trend={data?.PeakLoadSummary?.TrendPercentage}
          />
        </CardContent>
      </Card>
    </Grid>
  </>)
}